import { useState, useCallback } from 'react';
import { useUpload } from './useUpload';
import api from '../utils/api';

interface UseUploadCoverImageReturn {
  selectCoverImage: (imageId: string) => Promise<void>;
  error: string | null;
}

/**
 * Hook for persisting cover image (main image) selection on the upload page.
 *
 * Uses optimistic updates with rollback on failure, following the same
 * pattern as useUploadImageVisibility. When catalogId is null (user hasn't
 * uploaded yet), only sets local state without an API call.
 */
export function useUploadCoverImage(ownerId?: string | null): UseUploadCoverImageReturn {
  const { catalogId, selectedCoverId, setSelectedCoverId } = useUpload();
  const [error, setError] = useState<string | null>(null);

  const selectCoverImage = useCallback(
    async (imageId: string): Promise<void> => {
      const previousCoverId = selectedCoverId;

      // Optimistic update
      setSelectedCoverId(imageId);
      setError(null);

      if (!catalogId) {
        return;
      }

      const url = ownerId
        ? `/api/catalogs/${catalogId}/cover-image?owner_id=${ownerId}`
        : `/api/catalogs/${catalogId}/cover-image`;

      try {
        await api.patch(url, {
          imageId,
        });
      } catch (err) {
        // Revert on error
        setSelectedCoverId(previousCoverId);
        const message = err instanceof Error ? err.message : 'Failed to update cover image';
        setError(message);
      }
    },
    [catalogId, selectedCoverId, setSelectedCoverId, ownerId]
  );

  return { selectCoverImage, error };
}
